import { useEffect, useState } from 'react';
import { useTheme } from '../store/theme';

export type TelegramStatus = 'loading' | 'ready' | 'unavailable';

export interface TelegramContext {
  webApp: TelegramWebApp | null;
  status: TelegramStatus;
  ready: boolean;
  initData: string;
  userId: number | null;
  colorScheme: 'light' | 'dark';
  /** Telegram client platform (ios/android/tdesktop/…). 'unknown' outside a real
   *  Telegram client (e.g. the browser shim), '' until the SDK is bound. */
  platform: string;
}

const MAX_WAIT_MS = 8000;
const POLL_MS = 50;
// The Telegram chrome is painted to match the app background so the seam
// between the two disappears. Both values mirror the token layer in index.css,
// kept in sync by hand because Telegram wants a literal colour, not a variable.
const SURFACE_LIGHT = '#f3f2f8';
const SURFACE_DARK = '#121218';

/** Repaint the Telegram chrome for the theme the app is actually showing, which
 *  is not always Telegram's own — the user may have pinned light or dark. */
function paintChrome(wa: TelegramWebApp): void {
  const colour = useTheme.getState().resolved === 'dark' ? SURFACE_DARK : SURFACE_LIGHT;
  try {
    wa.setHeaderColor?.(colour);
    wa.setBackgroundColor?.(colour);
    wa.setBottomBarColor?.(colour);
  } catch {
    // noop — older clients lack some of these
  }
}

/** Push the device + Telegram-UI safe-area insets into CSS variables so the
 *  whole app can pad content away from the notch / home indicator. */
function applyInsets(wa: TelegramWebApp) {
  const sa = wa.safeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 };
  const cs = wa.contentSafeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 };
  const root = document.documentElement.style;
  root.setProperty('--tg-safe-top', `max(${sa.top + cs.top}px, env(safe-area-inset-top, 0px))`);
  root.setProperty(
    '--tg-safe-bottom',
    `max(${sa.bottom + cs.bottom}px, env(safe-area-inset-bottom, 0px))`,
  );
  root.setProperty('--tg-safe-left', `max(${sa.left + cs.left}px, env(safe-area-inset-left, 0px))`);
  root.setProperty(
    '--tg-safe-right',
    `max(${sa.right + cs.right}px, env(safe-area-inset-right, 0px))`,
  );
}

export function useTelegram(): TelegramContext {
  const [ctx, setCtx] = useState<TelegramContext>({
    webApp: null,
    status: 'loading',
    ready: false,
    initData: '',
    userId: null,
    colorScheme: 'light',
    platform: '',
  });

  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    function bind(wa: TelegramWebApp) {
      try {
        wa.ready();
        wa.expand();
        wa.disableVerticalSwipes?.();
        // Telegram's scheme is the input to the `system` preference, so it is
        // handed over before the chrome is painted from the result.
        useTheme.getState().setTelegramScheme(wa.colorScheme);
        paintChrome(wa);
        wa.MainButton?.hide?.();
        applyInsets(wa);
      } catch {
        // noop — older clients lack some methods
      }
      setCtx({
        webApp: wa,
        status: 'ready',
        ready: true,
        initData: wa.initData,
        userId: wa.initDataUnsafe.user?.id ?? null,
        colorScheme: wa.colorScheme,
        platform: wa.platform,
      });
      wa.onEvent('themeChanged', () => {
        // Only moves the app when the preference is `system`; the store decides.
        useTheme.getState().setTelegramScheme(wa.colorScheme);
        paintChrome(wa);
        setCtx((c) => ({ ...c, colorScheme: wa.colorScheme }));
      });
      const onInsets = () => applyInsets(wa);
      wa.onEvent('safeAreaChanged', onInsets);
      wa.onEvent('contentSafeAreaChanged', onInsets);
      wa.onEvent('fullscreenChanged', onInsets);
      wa.onEvent('viewportChanged', onInsets);
    }

    function tick() {
      if (cancelled) return;
      const wa = window.Telegram?.WebApp;
      if (wa) return bind(wa);
      if (Date.now() - start > MAX_WAIT_MS) {
        setCtx((c) => ({ ...c, status: 'unavailable' }));
        return;
      }
      setTimeout(tick, POLL_MS);
    }

    tick();
    return () => {
      cancelled = true;
    };
  }, []);

  return ctx;
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light') {
  try {
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
  } catch {
    /* noop outside Telegram */
  }
}
